import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../lib/api';

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'info' | 'success' | 'warning'; children: ReactNode }) {
  return (
    <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** Shows an API error (or nothing). Field errors are shown next to fields, see {@link Field}. */
export function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    const fieldMessages = Object.values(error.errors).flat();
    return (
      <Alert>
        <strong>{error.title}</strong>
        {error.detail && <span> {error.detail}</span>}
        {fieldMessages.length > 0 && (
          <ul>
            {fieldMessages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </Alert>
    );
  }
  return <Alert>Nie udało się połączyć z serwerem. Spróbuj ponownie później.</Alert>;
}

export function Loading({ label = 'Ładowanie…' }: { label?: string }) {
  return (
    <p className="loading" role="status" aria-live="polite">
      {label}
    </p>
  );
}

interface FieldProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  autoComplete?: string;
  hint?: string;
  error?: string | undefined;
  inputMode?: 'numeric' | 'decimal' | 'text' | 'email' | 'tel';
  maxLength?: number;
  min?: string;
  max?: string;
  /** Personal data (name, e-mail, phone): blurred while personal data is hidden. E-mail and phone fields always are (except sign-in). */
  pii?: boolean;
}

export function Field({ label, name, type = 'text', value, onChange, required, autoComplete, hint, error, inputMode, maxLength, min, max, pii }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        className={pii || ((type === 'email' || type === 'tel') && !autoComplete?.includes('username')) ? 'pii' : undefined}
        value={value}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        min={min}
        max={max}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && (
        <small id={hintId} className="hint">
          {hint}
        </small>
      )}
      {error && (
        <small id={errorId} className="field-error">
          {error}
        </small>
      )}
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  required,
  pii,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  /** The options are names of people: blurred while personal data is hidden. */
  pii?: boolean;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} className={pii ? 'pii' : undefined} value={value} required={required} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Page heading that receives focus after navigation (screen readers announce the new page). */
export function PageTitle({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <h1 ref={ref} tabIndex={-1}>
      {children}
    </h1>
  );
}

/** Accessible modal based on the native <dialog> element (focus trap and Esc handled by the browser). */
export function Dialog({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog ref={ref} aria-labelledby={titleId} onClose={onClose} onCancel={onClose}>
      <h2 id={titleId}>{title}</h2>
      {children}
    </dialog>
  );
}

/** Section that can be folded; its content mounts only while open (no requests for folded sections). */
export function Collapsible({ title, summary, children }: { title: string; summary?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="collapsible" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        <span className="collapsible-title">{title}</span>
        {summary != null && <span className="muted"> · {summary}</span>}
      </summary>
      {open && <div className="collapsible-body">{children}</div>}
    </details>
  );
}
